// @ts-strict-ignore
import { gql } from "@apollo/client";
import { USE_BACKEND_SEARCH } from "@dashboard/config";
import {
  SearchOrderVariantDocument,
  SearchOrderVariantQuery,
  SearchOrderVariantQueryVariables,
  SearchOrderVariantsDocument,
} from "@dashboard/graphql";
import makeTopLevelSearch from "@dashboard/hooks/makeTopLevelSearch";

// Default (client) search: Postgres full-text `filter.search`. Kept as the
// instant-revert fallback when USE_BACKEND_SEARCH is off.
export const searchOrderVariant = gql`
  query SearchOrderVariant(
    $channel: String!
    $first: Int!
    $query: String!
    $after: String
    $address: AddressInput
    $isPublished: Boolean
  ) {
    search: products(
      first: $first
      after: $after
      filter: { search: $query, isPublished: $isPublished }
      channel: $channel
    ) {
      edges {
        node {
          id
          name
          thumbnail {
            url
          }
          variants {
            id
            name
            sku
            pricing(address: $address) {
              priceUndiscounted {
                gross {
                  ...Money
                }
              }
              price {
                gross {
                  ...Money
                }
              }
              onSale
            }
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
      }
    }
  }
`;

// Backend search: the Meilisearch-backed `productVariantSearch` resolver. It
// ranks by relevance (accent/separator folding, typo tolerance, synonyms) and —
// crucially — resolves an exact SKU to that single variant, so the dialog can be
// precise about which variant is added. Publish/visibility scoping matches the
// `products` query; no stock filter, availability alone makes a variant addable.
export const searchOrderVariants = gql`
  query SearchOrderVariants(
    $channel: String!
    $first: Int!
    $query: String!
    $after: String
    $address: AddressInput
  ) {
    search: productVariantSearch(first: $first, after: $after, query: $query, channel: $channel) {
      edges {
        node {
          id
          name
          sku
          pricing(address: $address) {
            priceUndiscounted {
              gross {
                ...Money
              }
            }
            price {
              gross {
                ...Money
              }
            }
            onSale
          }
          product {
            id
            name
            thumbnail {
              url
            }
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
      }
    }
  }
`;

const useClientSearch = makeTopLevelSearch<
  SearchOrderVariantQuery,
  SearchOrderVariantQueryVariables
>(SearchOrderVariantDocument);

const useVariantSearch = makeTopLevelSearch(SearchOrderVariantsDocument);

// `productVariantSearch` returns a flat, relevance-ordered list of variants;
// the dialog groups variants under their product. Regroup the variant edges into
// the product-shaped `search` connection the dialog already consumes, preserving
// relevance order (first time a product is seen fixes its position) so an
// exact-SKU hit surfaces its product with just the matched variant.
const groupVariantsByProduct = (search): SearchOrderVariantQuery["search"] => {
  if (!search) {
    return search;
  }

  const order: string[] = [];
  const byProduct: Record<string, any> = {};

  for (const { node: variant } of search.edges) {
    const product = variant.product;

    if (!byProduct[product.id]) {
      order.push(product.id);
      byProduct[product.id] = {
        __typename: "Product",
        id: product.id,
        name: product.name,
        thumbnail: product.thumbnail,
        variants: [],
      };
    }

    byProduct[product.id].variants.push({
      __typename: "ProductVariant",
      id: variant.id,
      name: variant.name,
      sku: variant.sku,
      pricing: variant.pricing,
    });
  }

  return {
    __typename: "ProductCountableConnection",
    edges: order.map(id => ({ __typename: "ProductCountableEdge", node: byProduct[id] })),
    pageInfo: search.pageInfo,
  } as unknown as SearchOrderVariantQuery["search"];
};

const useBackendSearch: typeof useClientSearch = opts => {
  const { loadMore, search, result } = useVariantSearch(opts as never);

  return {
    loadMore,
    search,
    result: {
      ...result,
      data: result.data
        ? { ...result.data, search: groupVariantsByProduct(result.data.search) }
        : result.data,
    },
  } as ReturnType<typeof useClientSearch>;
};

export const useOrderVariantSearch = USE_BACKEND_SEARCH ? useBackendSearch : useClientSearch;
